// T31 — temporal replay over an ALREADY-decoded feature set (no re-decode per tick). Reuses the
// charts' datetime categorization (`getCategoryFromDataType` / `detectCategoryFromValues`) to find
// the FIRST column that categorizes as a datetime AND has >=2 distinct parseable values in the
// mapped rows — the control never renders when nothing qualifies (progressive disclosure).
// Playback advances the cursor over the column's [min, max] range in ~60 wall-clock steps at
// `speed`x; `visibleKeys` is a cheap Set rebuild per tick (row index <= cursor) over the EXISTING
// rows — zero decode work. `findCandidateTimeColumnId` (A7/NG9) picks the candidate from METADATA
// alone (declared data type, no value sampling) so callers can join it into a windowed fetch's
// needed-columns list BEFORE any row has been fetched.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCategoryFromDataType, detectCategoryFromValues } from '../../charts/utils/columnTypeDetection';

export type TemporalReplayColumn = { id: string; dataType?: string };
export type TemporalReplaySpeed = 1 | 2 | 4;

export type TemporalReplay = {
  timeColumn: string | null;
  range: [number, number] | null;
  cursor: number;
  playing: boolean;
  play: () => void;
  pause: () => void;
  seek: (t: number) => void;
  speed: TemporalReplaySpeed;
  setSpeed: (speed: TemporalReplaySpeed) => void;
  /** Row indices (== feature ids, T13/T14's 1:1 row-index-to-feature-id convention) whose time
   *  value is <= the cursor. Empty when `timeColumn` is null. */
  visibleKeys: Set<string | number>;
};

const STEPS = 60;
const BASE_STEP_MS = 100; // speed=1 covers the whole range in STEPS * BASE_STEP_MS = 6s

function parseTimestamp(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime();
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Date.parse(trimmed);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

/** A7/NG9 — the FIRST column whose DECLARED data type categorizes as datetime. Metadata only (no
 *  row sampling), so it can be chosen before any windowed fetch happens and joined into that
 *  fetch's needed-columns list. */
export function findCandidateTimeColumnId(columns: TemporalReplayColumn[]): string | null {
  for (const col of columns) {
    if (getCategoryFromDataType(col.dataType) === 'date') return col.id;
  }
  return null;
}

/** First datetime-categorized column (declared type, else sampled values — mirrors the charts'
 *  own fallback order) with >=2 distinct parseable timestamps in `rows`. */
function findTimeColumn(
  rows: Record<string, unknown>[],
  columns: TemporalReplayColumn[],
): { id: string; range: [number, number] } | null {
  for (const col of columns) {
    const declared = getCategoryFromDataType(col.dataType);
    const category = declared !== 'unknown' ? declared : detectCategoryFromValues(rows, col.id).category;
    if (category !== 'date') continue;

    let min = Infinity;
    let max = -Infinity;
    const distinct = new Set<number>();
    for (const row of rows) {
      const t = parseTimestamp(row[col.id]);
      if (t === null) continue;
      distinct.add(t);
      if (t < min) min = t;
      if (t > max) max = t;
    }
    if (distinct.size >= 2) return { id: col.id, range: [min, max] };
  }
  return null;
}

export function useTemporalReplay(
  rows: Record<string, unknown>[],
  columns: TemporalReplayColumn[],
): TemporalReplay {
  const found = useMemo(() => findTimeColumn(rows, columns), [rows, columns]);
  const timeColumn = found?.id ?? null;
  const range = found?.range ?? null;

  const [cursor, setCursor] = useState<number>(() => range?.[0] ?? 0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<TemporalReplaySpeed>(1);

  // A new result set / time column ⇒ restart at the beginning, not mid-way through the old range.
  useEffect(() => {
    setCursor(range?.[0] ?? 0);
    setPlaying(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeColumn, range?.[0], range?.[1]]);

  useEffect(() => {
    if (!playing || !range) return;
    const stepValue = (range[1] - range[0]) / STEPS;
    const id = setInterval(() => {
      setCursor((c) => {
        const next = c + stepValue;
        if (next >= range[1]) {
          setPlaying(false);
          return range[1];
        }
        return next;
      });
    }, BASE_STEP_MS / speed);
    return () => clearInterval(id);
  }, [playing, speed, range]);

  const play = useCallback(() => {
    if (!range) return;
    setCursor((c) => (c >= range[1] ? range[0] : c));
    setPlaying(true);
  }, [range]);
  const pause = useCallback(() => setPlaying(false), []);
  const seek = useCallback(
    (t: number) => {
      if (!range) return;
      setPlaying(false);
      setCursor(Math.min(range[1], Math.max(range[0], t)));
    },
    [range],
  );

  const visibleKeys = useMemo(() => {
    const keys = new Set<string | number>();
    if (!timeColumn) return keys;
    rows.forEach((row, i) => {
      const t = parseTimestamp(row[timeColumn]);
      if (t !== null && t <= cursor) keys.add(i);
    });
    return keys;
  }, [rows, timeColumn, cursor]);

  return { timeColumn, range, cursor, playing, play, pause, seek, speed, setSpeed, visibleKeys };
}
